import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { RequestStatus } from "@companion-core/shared/app/Components/Requests/RequestStatus";
import { Banner } from "@companion-core/shared/app/Components/Banner";
import { RequestDetailLayoutProps } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestDetailLayout/requestDetailLayout.model";
import { RequestDetailContent } from "@companion-core/shared/app/Components/Requests/RequestDetail/requestDetail.model";
import { useTransparentHeader } from "@companion-core/shared/app/Hooks/transparentHeader";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { useRequestCard } from "@companion-core/shared/app/Components/Requests/RequestCard/hooks/useRequestCard";
import { htmlToPlainText } from "@companion-core/shared/app/Utils/htmlToPlainText";

import "@companion-core/web/src/Assets/Styles/scss/myRequestsDetailLayout.scss";

export const RequestDetailLayout = ({
  data,
  children,
  detailContent,
}: RequestDetailLayoutProps) => {
  useTransparentHeader();
  const { t } = useTranslation();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { detailTitle } = useRequestCard(data);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const status = useMemo(
    () =>
      !data.has_digital_proposal && data.status === "recommended"
        ? "recommended-by-mail"
        : data.status,
    [data]
  );
  const subjectId = `request-detail-layout__subject-${data.id}`;
  const statusId = `request-detail-layout__status-${data.id}`;
  const isMainDetailView = !detailContent || detailContent === RequestDetailContent.detail;
  const plainSubject = useMemo(() => htmlToPlainText(detailTitle), [detailTitle]);
  const statusLabel = useMemo(() => t(`request.my-requests.statusV2.${status}`), [status, t]);

  useEffect(() => {
    if (!isMainDetailView) return;

    document.title = `${plainSubject} – ${statusLabel}`;

    const focusTimeout = window.setTimeout(() => {
      headingRef.current?.focus({ preventScroll: true });
    }, 0);

    return () => window.clearTimeout(focusTimeout);
  }, [data.id, plainSubject, statusLabel, isMainDetailView]);

  return (
    <div data-testid="request-detail-layout" className="request-detail__layout">
      <Banner
        title={
          <h1
            ref={headingRef}
            id={subjectId}
            tabIndex={-1}
            aria-describedby={statusId}
            data-testid="request-detail-layout__subject"
            className={`title-${isMobileSize ? "5" : "7"} request-detail__subject`}>
            {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
            <span dangerouslySetInnerHTML={{ __html: detailTitle }} />
          </h1>
        }
        subtitle={<RequestStatus status={status} id={statusId} />}
        imageUrl={`https://res.cloudinary.com/john-paul/image/upload/v1730213827/Tech/My%20Requests/${data.family}/${data.sub_family ?? data.family}.jpg`}
        imageUrlFallback="https://res.cloudinary.com/john-paul/image/upload/v1730295928/Tech/My%20Requests/other-families.jpg"
        boxShadow={true}
      />
      {children}
    </div>
  );
};
